// ────────────────────────────────────────────────────────────────────────────
// pdf.js - DEFERRED loader (dynamic import + Blob-URL worker wrapper)
// ────────────────────────────────────────────────────────────────────────────
// pdfjs-dist is ~1.4 MB and was previously imported at module top level,
// blocking initial paint on every page (Dashboard, clients list, etc.)
// even though only a handful of modal/stage flows actually parse PDFs.
// It is now dynamically imported on first call via loadPdfjs() below,
// so the main bundle no longer carries the pdf.js payload.
//
// pdfjs-dist v4+ ships the worker as ES modules only (.mjs). Browsers enforce
// strict MIME-type checking on module scripts - any response with
// application/octet-stream, text/plain, or a missing Content-Type gets
// rejected with "Failed to load module script...". This is a production
// hazard: some web servers (unpatched nginx, CDNs with odd defaults, file://
// loads) don't map .mjs to application/javascript, and all four pdf.js
// fallback strategies (real worker, fake worker via dynamic import, relaxed
// options, canvas render) hit the same wall.
//
// Fix: fetch the worker source at runtime, wrap it in a Blob with an
// explicit application/javascript type, and hand pdf.js a blob: URL. The
// browser trusts the Blob's declared type, so module-script MIME checks
// pass regardless of what the origin server reported. If the initial
// fetch fails entirely (offline, 404), we fall back to the raw URL so
// correctly-configured servers keep working.
//
// loadPdfjs() is idempotent - the promise is cached, so concurrent
// callers share one bundle download + one worker configuration.

// Pricing Engine (DEC-PE-009): catalogue-first cost-gen steer + the Slice 0
// markup gate, both read through the single flag site in feature-flags.ts.
// The §0 coupling warning below MUST share cost-math's reader: a previous
// readFlagDefaultOff read here inverted the default (the runtime gate is
// default-ON) and fired the warning spuriously on every fresh device.
import { isCatalogueFirstCostingEnabled } from './feature-flags';
import { isTrustedPricingItem, isVisiblePricingItem } from '../services/pricing-trust.js';
import { orderPdfTextItems } from './pdf-layout-text';
import { shouldUsePdfRangeTransport } from '../services/reference-file-import-policy';
import { isManuallyMarkedUp } from '../services/photo-resolution-policy.js';
import { resolveClientScopeDescription } from './scope-descriptions';
import { estimatorNotesHeaderRole, estimatorNotesPromptText } from './estimator-notes-price-strip';
import { isRequestOrScopeDocument } from './document-classification';
import { detectPlanByContent } from './report-plan-content-detector';
import { aiDocumentLabel } from './ai-document-label';

let pdfjsLoaderPromise = null;
// Exported for viewers that draw a local File (the New RFQ sheet's page
// preview), so every pdf.js user shares this one worker set-up.
export async function loadPdfjs() {
    if (pdfjsLoaderPromise) return pdfjsLoaderPromise;
    pdfjsLoaderPromise = (async () => {
        const [pdfjsLib, workerUrlMod] = await Promise.all([
            import('pdfjs-dist'),
            import('pdfjs-dist/build/pdf.worker.min.mjs?url'),
        ]);
        const pdfWorkerUrl = workerUrlMod.default;
        try {
            const resp = await fetch(pdfWorkerUrl);
            if (!resp.ok) throw new Error(`worker fetch ${resp.status}`);
            const code = await resp.text();
            if (code.length < 1000) throw new Error('worker payload too small');
            const blob = new Blob([code], { type: 'application/javascript' });
            pdfjsLib.GlobalWorkerOptions.workerSrc = URL.createObjectURL(blob);
            console.log('[PDF] Worker configured via Blob URL (MIME-safe path)');
        } catch (err) {
            console.warn(`[PDF] Blob-URL worker setup failed (${err.message}), using raw URL`);
            pdfjsLib.GlobalWorkerOptions.workerSrc = pdfWorkerUrl;
        }
        // Council #6 fix (v5.19.60): suppress benign PDF.js warnings like
        // "TT: undefined function: N" which originate from upstream PDF
        // authoring tools writing malformed TT font bytecode. PDF.js renders
        // these documents fine via fallback hinting; the warnings are
        // cosmetic noise that drown out actionable signals in the console.
        // verbosity 0 = errors only; 1 = warnings + errors; 5 = info+.
        try {
            if (pdfjsLib?.VerbosityLevel?.ERRORS !== undefined) {
                pdfjsLib.verbosity = pdfjsLib.VerbosityLevel.ERRORS;
            } else {
                pdfjsLib.verbosity = 0;
            }
        } catch { /* property might be read-only on some pdfjs versions — non-fatal */ }
        return pdfjsLib;
    })();
    return pdfjsLoaderPromise;
}

const PDF_RANGE_CHUNK_BYTES = 1024 * 1024;

/**
 * Give pdf.js a Blob/File-backed random-access transport for large PDFs.
 * Each request allocates only the requested slice; the whole customer file is
 * never copied into a contiguous browser ArrayBuffer or base64 string.
 */
export async function createPdfLoadingTask(pdfjsLib, file, options = {}) {
    if (shouldUsePdfRangeTransport(file)) {
        if (typeof file?.slice !== 'function' || typeof pdfjsLib?.PDFDataRangeTransport !== 'function') {
            throw new Error('Large PDF range transport is unavailable in this browser');
        }
        class BlobPdfRangeTransport extends pdfjsLib.PDFDataRangeTransport {
            constructor(source) {
                super(source.size, null, false, source.name || 'document.pdf');
                this.source = source;
                this.cancelled = false;
                this.loadedHighWater = 0;
            }

            requestDataRange(begin, end) {
                if (this.cancelled) return;
                const safeBegin = Math.max(0, Number(begin) || 0);
                const safeEnd = Math.min(this.source.size, Math.max(safeBegin, Number(end) || safeBegin));
                this.source.slice(safeBegin, safeEnd).arrayBuffer()
                    .then((buffer) => {
                        if (this.cancelled) return;
                        const chunk = new Uint8Array(buffer);
                        this.loadedHighWater = Math.max(this.loadedHighWater, safeBegin + chunk.byteLength);
                        this.onDataRange(safeBegin, chunk);
                        this.onDataProgress(this.loadedHighWater, this.source.size);
                    })
                    .catch(() => {
                        if (!this.cancelled) this.onDataRange(safeBegin, null);
                    });
            }

            abort() {
                this.cancelled = true;
            }
        }

        const range = new BlobPdfRangeTransport(file);
        return pdfjsLib.getDocument({
            ...options,
            range,
            length: file.size,
            rangeChunkSize: PDF_RANGE_CHUNK_BYTES,
            disableRange: false,
            disableStream: true,
            disableAutoFetch: true,
        });
    }

    const arrayBuffer = await file.arrayBuffer();
    return pdfjsLib.getDocument({ ...options, data: arrayBuffer });
}

async function loadPdfDocument(pdfjsLib, file, options = {}) {
    return (await createPdfLoadingTask(pdfjsLib, file, options)).promise;
}

async function destroyPdfDocument(pdf, label = 'PDF') {
    try {
        if (pdf && typeof pdf.destroy === 'function') await pdf.destroy();
    } catch (error) {
        console.warn(`[${label}] document cleanup failed:`, error?.message || error);
    }
}

// Duplicate file detection - compares name + size (bytes) against existing photos/documents
export const isDuplicateFile = (fileName, fileSize, existingPhotos, existingDocuments) => {
    if (!fileName) return false;
    if (existingPhotos.some(p => (p.originalName || p.name) === fileName && p.originalSize === fileSize)) return true;
    if (existingDocuments.some(d => d.name === fileName && d.size === fileSize)) return true;
    return false;
};

// Yield control to the browser's main thread to prevent freezing during heavy loops
export const yieldToMain = () => new Promise(resolve => setTimeout(resolve, 0));

// pdf.js schedules a 'display' render with requestAnimationFrame, and browsers
// pause requestAnimationFrame entirely while a tab is hidden. An extraction
// render (OCR / page images) then never settles: a queued Quote Run hangs in
// Stage 1 with no error, and Stop cannot interrupt it because nothing observes
// the abort. 'print' intent renders the same page without requestAnimationFrame
// (pdfjs-dist 4.10.38 build/pdf.mjs: `useRequestAnimationFrame: !intentPrint`),
// so extraction keeps working when the operator switches to another window.
// On-screen previews (PdfPreview, LegacyPdfPreviewFrame) keep the default.
export const PDF_EXTRACTION_RENDER_INTENT = 'print';

// Promise race with timeout
export const withTimeout = (promise, ms, label = 'Operation') =>
    Promise.race([
        promise,
        new Promise((_, reject) => setTimeout(() => reject(new Error(`${label} timed out after ${ms / 1000}s`)), ms))
    ]);

// ── Canvas → JPEG encode (non-blocking) ─────────────────────────────────────
// `canvas.toDataURL()` is SYNCHRONOUS: for a large canvas (a full-page PDF scan,
// a 2.0×-scaled A1 plan, or a high-res embedded photo) the JPEG encode blocks the
// main thread for seconds and the tab goes "page unresponsive" (Peter, v5.41.38,
// during reference-file classification). `canvas.toBlob()` runs the same encode
// asynchronously (off the main thread in modern browsers), so prefer it and fall
// back to the synchronous path only where toBlob is unavailable.
const MAX_RENDER_DIM = 5000;          // px cap on a rendered PDF-page canvas edge (prevents OOM / multi-second render on A0/A1)
// Raw RGBA conversion is an un-yieldable main-thread loop. Above this boundary
// the caller must render the containing PDF page instead; it is a routing
// boundary, never a content-omission ceiling. ImageBitmap-backed embeds can be
// downscaled directly and therefore stay on the embedded-image lane.
const RAW_EMBED_MAIN_THREAD_DECODE_PIXELS = 30_000_000;

/**
 * Encode a canvas to a `data:image/jpeg` URL WITHOUT blocking the main thread.
 * Uses the async `canvas.toBlob()` + FileReader path when available; falls back
 * to synchronous `toDataURL` only when toBlob is missing.
 * @param {HTMLCanvasElement} canvas
 * @param {number} [quality=0.85]
 * @returns {Promise<string>}
 */
export async function canvasToJpegDataUrl(canvas, quality = 0.85) {
    if (canvas && typeof canvas.toBlob === 'function') {
        const blob = await new Promise((resolve) => {
            try { canvas.toBlob(resolve, 'image/jpeg', quality); }
            catch { resolve(null); }
        });
        if (blob) {
            return await new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = () => resolve(String(reader.result || ''));
                reader.onerror = () => reject(reader.error || new Error('FileReader failed reading canvas blob'));
                reader.readAsDataURL(blob);
            });
        }
    }
    // Fallback: synchronous encode (older browsers / toBlob unavailable).
    return canvas.toDataURL('image/jpeg', quality);
}

// Preserve legacy array return shapes while making traversal truth available
// to every caller. Non-enumerable metadata does not alter JSON payloads,
// iteration, deep equality, or existing array consumers.
function attachPdfCoverage(items, coverage) {
    if (!Array.isArray(items)) return items;
    try {
        Object.defineProperty(items, 'coverage', {
            value: coverage,
            configurable: true,
            enumerable: false,
            writable: false,
        });
    } catch { /* frozen/test arrays can still be returned without decoration */ }
    return items;
}

// Helper: read a File object as a data URL
export const readFileAsDataURL = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => resolve(e.target.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
});

/**
 * Clamp any input to a finite integer in [min, max], falling back to a
 * caller-supplied default for non-numeric values. Used by every defensive
 * AI-response normaliser in the codebase to keep confidence scores, counts,
 * and other bounded numeric fields well-formed.
 */
export function clampNum(v, min, max, fallback) {
    const n = Number(v);
    if (!Number.isFinite(n)) return fallback;
    return Math.max(min, Math.min(max, Math.round(n)));
}

/**
 * Escape XML/HTML special characters in a string to prevent prompt injection
 * via user-supplied content that could break XML tag structures in LLM prompts.
 *
 * @param {string} s The string to escape.
 * @returns {string} Escaped string safe for XML context.
 */
export function escapeXml(s) {
    if (!s) return '';
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/**
 * Truncate a string to at most `maxChars` UTF-16 code units WITHOUT splitting
 * a surrogate pair. JavaScript strings are UTF-16, so a naive substring(0, N)
 * can leave a lone high surrogate at the end, producing an invalid string
 * that breaks JSON serialisation and emoji rendering downstream.
 *
 * If the cut would land in the middle of a surrogate pair we back off by one
 * code unit so the pair stays intact (or is removed cleanly).
 *
 * @param {string} text     The text to truncate (null/undefined → empty string).
 * @param {number} maxChars Hard upper bound on the result length.
 * @returns {string}        A safely truncated string ≤ maxChars in length.
 */
export function truncateSafeText(text, maxChars) {
    if (text == null) return '';
    const s = String(text);
    if (!Number.isFinite(maxChars) || maxChars <= 0) return '';
    if (s.length <= maxChars) return s;
    let cut = maxChars;
    // 0xD800-0xDBFF are high surrogates (the FIRST half of a surrogate pair).
    // If the last retained char is a high surrogate the next char (which we're
    // about to drop) is its low-surrogate partner - back off by one to keep
    // the truncation on a valid code-point boundary.
    const lastCode = s.charCodeAt(cut - 1);
    if (lastCode >= 0xD800 && lastCode <= 0xDBFF) {
        cut -= 1;
    }
    return s.substring(0, cut);
}

// A. Classify user intent to determine which context blocks to include
export const classifyUserIntent = (userMessage) => {
    const msg = (userMessage || '').toLowerCase();
    return {
        pricing: /price|cost|rate|how much|budget|estimate|charge|amount|expensive|cheap|margin|markup|total|subtotal|\$\d/i.test(msg),
        scopeStructure: /\b(add|remove|delete|reorder|move|duplicate|merge|split|new item|create)\b/i.test(msg),
        textEditing: /description|title|word|rewrit|rephrase|professional|improve|fix|grammar|spell|clarif/i.test(msg),
        photos: /photo|image|picture|assign|attach|markup|evidence|snapshot/i.test(msg),
        inclusions: /inclusion|include|what.*included|add.*to.*list|bullet/i.test(msg),
        question: /\?|what is|how do|why|explain|tell me|can you/i.test(msg),
        global: /\b(all|every|each|total|overall|entire|whole|sum|adjust.*all|review.*all)\b/i.test(msg),
    };
};

// B. Filter business pricing to only relevant trades/materials
const PRICING_KEYWORD_MAP = {
    tile: ['tile', 'tiling', 'grout'],
    brick: ['brick', 'masonry', 'blockwork'],
    concrete: ['concrete', 'slab', 'footing', 'foundation'],
    plaster: ['plaster', 'gyprock', 'wall lining', 'cornice'],
    render: ['render', 'rendering', 'acrylic'],
    paint: ['paint', 'painting', 'painter', 'coat', 'primer', 'undercoat', 'dulux', 'weathershield', 'acrylic'],
    roof: ['roof', 'roofing', 'roof tile', 'gutter', 'flashing', 'fascia', 'barge', 'eave', 'eaves', 'soffit', 'sarking', 'truss', 'colorbond'],
    window: ['window', 'glazing', 'glass', 'louvre'],
    waterproof: ['waterproof', 'membrane', 'leak', 'seal', 'damp', 'balcony'],
    timber: ['timber', 'wood', 'pine', 'h3', 'carpenter', 'carpentry', 'trim', 'mould', 'batten', 'grounds', 'decking', 'framing', 'structural', 'hardwood'],
    steel: ['steel', 'rhs', 'metal', 'weld', 'lintel'],
    fence: ['fence', 'fencing', 'colorbond', 'paling'],
    door: ['door', 'lock', 'entrance', 'cavity slider'],
    floor: ['floor', 'flooring', 'carpet', 'vinyl', 'epoxy', 'polish'],
    cladding: ['cladding', 'fibre cement', 'fiber cement', 'f/c', 'f.c', 'fc sheet', 'eave', 'eaves', 'soffit', 'weatherboard', 'scyon'],
    insulation: ['insulation', 'batts', 'thermal'],
    demolition: ['demo', 'demolition', 'strip', 'remove', 'removal', 'skip', 'waste'],
    excavation: ['excavat', 'dig', 'soil', 'backfill'],
    scaffold: ['scaffold', 'scaffolder', 'tower', 'access', 'abseil', 'swing stage'],
    asbestos: ['asbestos', 'acm', 'hazardous material', 'clearance certificate'],
    pest: ['pest', 'possum', 'wildlife', 'fauna', 'vermin', 'exclusion mesh'],
    electrical: ['electric', 'power', 'wiring', 'switch', 'light', 'circuit'],
    plumbing: ['plumb', 'pipe', 'drain', 'tap', 'toilet', 'cistern', 'sewer'],
    aircon: ['air con', 'hvac', 'a/c', 'split system', 'ducted'],
    kitchen: ['kitchen', 'cupboard', 'cabinet', 'benchtop', 'splashback'],
    landscape: ['landscape', 'garden', 'paving', 'retaining'],
    strata: ['strata', 'common area', 'common property', 'body corporate'],
};

// Product names in the Francis authority are often brand-led rather than a
// literal repeat of the scope wording (for example Bondcrete / MonoTop for
// repointing, or Sikaflex for a polyurethane joint). These small deterministic
// concept groups keep those approved identities in the bounded prompt without
// sending the model the entire rate card.
const PRICING_CONCEPT_GROUPS = Object.freeze([
    {
        context: ['seal', 'joint', 'window perimeter', 'waterproof', 'membrane'],
        rows: [
            'sikaflex', 'sealant', 'backing rod', 'bond breaker',
            'bondcrete', 'monotop', 'mortar',
            'membrane', 'waterproof', 'davco', 'tremco',
        ],
    },
    {
        context: ['stormwater', 'downpipe', 'drain', 'channel', 'pipe'],
        rows: ['stormwater', 'downpipe', 'drain', 'channel', 'pipe', 'concrete', 'bondcrete'],
    },
    {
        context: ['brick', 'masonry', 'perpend', 'repoint', 'mortar'],
        rows: ['brick', 'masonry', 'bondcrete', 'monotop', 'mortar', 'concrete'],
    },
    {
        context: ['paving', 'paver', 'tile', 'screed'],
        rows: ['paving', 'paver', 'landscaper', 'tile', 'screed', 'concrete'],
    },
    {
        context: ['render', 'make good', 'plaster', 'paint'],
        rows: ['render', 'plaster', 'paint', 'primer', 'painter'],
    },
]);

export const filterRelevantPricing = (pricingData, context = {}) => {
    if (!pricingData) return null;
    const { userMessage = '', scopeItems = [], maxItems = 20 } = context;

    const searchText = [
        userMessage,
        ...scopeItems.map(s => `${s.title || ''} ${s.description || ''}`),
    ].join(' ').toLowerCase();

    const relevantCategories = new Set();
    for (const [category, keywords] of Object.entries(PRICING_KEYWORD_MAP)) {
        if (keywords.some(k => searchText.includes(k))) {
            relevantCategories.add(category);
        }
    }

    const isPricingQuestion = /price|cost|rate|how much|budget|estimate|charge|amount/i.test(userMessage);

    if (relevantCategories.size === 0 && !isPricingQuestion) {
        return {
            material_costs: (pricingData.material_costs || []).slice(0, 10),
            labour_rates: (pricingData.labour_rates || []).slice(0, 8),
            equipment: (pricingData.equipment || []).slice(0, 3),
            subcontractors: (pricingData.subcontractors || []).slice(0, 3),
            administration: pricingData.administration,
            _note: 'No specific category match - showing representative rates.'
        };
    }

    const normaliseSearch = (value) => String(value || '')
        .toLowerCase()
        .replace(/\bf\s*[/.]\s*c\b|\bfc\b/g, 'fibre cement')
        .replace(/\bcarpentry\b/g, 'carpenter')
        .replace(/[^a-z0-9]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
    const normalisedSearchText = normaliseSearch(searchText);
    const activeConceptGroups = PRICING_CONCEPT_GROUPS.filter(group => (
        group.context.some(term => normalisedSearchText.includes(normaliseSearch(term)))
    ));
    const matchesProjectConcept = (text) => {
        const lower = normaliseSearch(text);
        return activeConceptGroups.some(group => (
            group.rows.some(term => lower.includes(normaliseSearch(term)))
        ));
    };
    const matchesCategory = (text) => {
        const lower = normaliseSearch(text);
        for (const cat of relevantCategories) {
            if ((PRICING_KEYWORD_MAP[cat] || []).some(k => lower.includes(normaliseSearch(k)))) return true;
        }
        return false;
    };

    // Category membership is necessary but not sufficient once the Francis
    // authority contains 100+ rows. Rank the rows by actual overlap with this
    // project before applying the prompt-size cap; alphabetical slicing sent
    // Dulwich an unrelated first page (wall tiles, windows and flooring) while
    // omitting its F/C soffit, H3 timber, painting and scaffold identities.
    const scoreRow = (value) => {
        const rowText = normaliseSearch(value);
        const tokens = rowText.split(' ').filter(token => (token.length >= 3 || token === 'h3')
            && !['per', 'supply', 'install', 'including', 'standard', 'rate'].includes(token));
        let score = tokens.reduce((sum, token) => (
            normalisedSearchText.includes(token) ? sum + 4 : sum
        ), 0);
        for (const category of relevantCategories) {
            const categoryKeywords = PRICING_KEYWORD_MAP[category] || [];
            if (categoryKeywords.some(keyword => rowText.includes(normaliseSearch(keyword)))) score += 2;
        }
        for (const group of activeConceptGroups) {
            if (group.rows.some(term => rowText.includes(normaliseSearch(term)))) score += 12;
        }
        const categoryRoleBoosts = {
            timber: ['carpenter'],
            paint: ['painter'],
            scaffold: ['scaffolder'],
            asbestos: ['asbestos removal', 'clearance certificate'],
            pest: ['pest control'],
        };
        for (const category of relevantCategories) {
            if ((categoryRoleBoosts[category] || []).some(role => rowText.includes(role))) score += 8;
        }
        return score;
    };
    const rankRows = (rows, nameOf) => rows.sort((a, b) => {
        const scoreDelta = scoreRow(nameOf(b)) - scoreRow(nameOf(a));
        return scoreDelta || String(nameOf(a) || '').localeCompare(String(nameOf(b) || ''));
    });

    // Sort alphabetically for deterministic selection when slicing to maxItems
    const filteredMaterials = rankRows(
        (pricingData.material_costs || []).filter(m => matchesCategory(m.item) || matchesProjectConcept(m.item)),
        row => row.item,
    );
    const filteredLabour = (pricingData.labour_rates || []).filter(l =>
        matchesCategory(l.trade)
        || matchesProjectConcept(l.trade)
        || normalisedSearchText.includes(normaliseSearch(l.trade))
        || /\blabourer\b/i.test(String(l.trade || ''))
    );
    rankRows(filteredLabour, row => row.trade);
    const filteredEquipment = rankRows(
        (pricingData.equipment || []).filter(e => (
            matchesCategory(e.item)
            || matchesProjectConcept(e.item)
            || /waste.*protection/i.test(String(e.item || ''))
        )),
        row => row.item,
    );
    const filteredSubcontractors = rankRows((pricingData.subcontractors || []).filter(s =>
        matchesCategory(s.trade || s.name || '')
        || matchesProjectConcept(s.trade || s.name || '')
        || searchText.includes((s.trade || s.name || '').toLowerCase())
    ), row => row.trade || row.name);

    const totalFiltered = filteredMaterials.length + filteredLabour.length + filteredEquipment.length + filteredSubcontractors.length;

    if (totalFiltered === 0 && isPricingQuestion) {
        return {
            material_costs: (pricingData.material_costs || []).slice(0, 15),
            labour_rates: (pricingData.labour_rates || []).slice(0, 10),
            equipment: (pricingData.equipment || []).slice(0, 5),
            subcontractors: (pricingData.subcontractors || []).slice(0, 5),
            administration: pricingData.administration,
            _note: 'Showing top items. Ask about specific trades for full rates.'
        };
    }

    return {
        material_costs: filteredMaterials.slice(0, maxItems),
        labour_rates: filteredLabour.slice(0, maxItems),
        equipment: filteredEquipment.slice(0, maxItems),
        subcontractors: filteredSubcontractors.slice(0, maxItems),
        administration: pricingData.administration,
    };
};

// C. Format filtered pricing into prompt text
//
// opts.catalogueFirst (Pricing Engine, DEC-PE-009): when true, the catalogue block
// leads with a directive telling the AI to SELECT each cost line from the rate book
// by EXACT name + rate (binding fires by name) and to name genuine gaps precisely
// instead of inventing a price. Defaults to the `qm_catalogue_first_costing` flag
// (default ON since v5.41.76) — the flag-OFF block stays byte-identical to the
// pre-steer text. See docs/pricing-engine/MATCHING-FIX-PROMPT-DRAFT.md.
export const formatPricingForPrompt = (pricingData, opts = {}) => {
    if (!pricingData) return '';
    const catalogueFirst = opts.catalogueFirst ?? isCatalogueFirstCostingEnabled();
    const costStackContract = 'Cost stack contract: materials + labour + equipment + subcontractors + other = direct_cost_total. Keep subcontracted external trade packages in subcontractors, not labour. Apply contingency/overhead/margin after the direct-cost lines.\n';
    let block;
    if (catalogueFirst) {
        block = '\nBUSINESS PRICING - AUTHORITATIVE RATE CATALOGUE (CATALOGUE-FIRST COSTING - use the builder\'s rates, do not invent them):\n';
        block += 'For EVERY materials / labour / equipment / subcontractor line you emit: if the catalogue below contains the item or trade that line represents, you MUST name the line using the catalogue\'s EXACT item/trade name and use its rate verbatim - use "Carpenter" (the catalogue trade), never a synonym like "Builder" or "Tradesman"; use the catalogue\'s exact item name, never a paraphrase. The system binds your line to the rate book BY NAME, so a paraphrase silently loses the builder\'s rate.\n';
        block += 'The catalogue rate is the builder\'s FINAL charge-out rate - do NOT substitute your own number when a catalogue entry exists. Method-first refinement changes the QUANTITIES (hours, m2, linear metres) and WHICH catalogue item you select (premium vs minimum per positioning), NEVER the rate itself. This takes precedence over any instruction to treat an initial estimate as a "starting point": refine quantities and selection, but never invent or override a catalogue rate.\n';
        block += 'Only when NOTHING in the catalogue covers the work may you add an off-catalogue line. Name it specifically - the actual material or trade, never a vague "allowance" or "Builder" - give a conservative best estimate, and know these lines are surfaced to the builder as gaps that need his price.\n';
        block += costStackContract;
    } else {
        block = '\nBUSINESS PRICING - AUTHORITATIVE RATE CATALOGUE:\n';
        block += costStackContract;
    }
    // The same rule the matchers and the Stage 5 certifier use. An "AI Review"
    // estimate is not the builder's rate, so it must not be offered here as one.
    const trusted = (rows = []) => (Array.isArray(rows) ? rows : []).filter(isTrustedPricingItem);
    const cfg = pricingData.costingConfig || {};
    if (cfg.overhead || cfg.contingency || Array.isArray(cfg.marginBrackets)) {
        block += `Costing rules: overhead=${cfg.overhead?.type || 'default'} ${cfg.overhead?.value ?? ''}; contingency=${cfg.contingency?.type || 'default'} ${cfg.contingency?.value ?? ''}; margin bands=${(cfg.marginBrackets || []).map(b => `${b.maxValue ?? 'above'}:${b.marginPct}%`).join(', ') || 'default'}\n`;
    }
    const materials = trusted(pricingData.material_costs);
    const labour = trusted(pricingData.labour_rates);
    const equipment = trusted(pricingData.equipment);
    const subcontractors = trusted(pricingData.subcontractors);
    if (materials.length) {
        block += 'Materials:\n' + materials.map(m =>
            `  - ${m.item}: $${m.cost} ${m.unit}`
        ).join('\n') + '\n';
    }
    if (labour.length) {
        block += 'Labour Rates:\n' + labour.map(l =>
            `  - ${l.trade}: $${l.rate} ${l.unit}`
        ).join('\n') + '\n';
    }
    if (equipment.length) {
        block += 'Equipment:\n' + equipment.map(e =>
            `  - ${e.item}: $${e.cost} ${e.unit}`
        ).join('\n') + '\n';
    }
    if (subcontractors.length) {
        block += 'Subcontractors:\n' + subcontractors.map(s =>
            `  - ${s.trade || s.name}: $${s.rate} ${s.unit}`
        ).join('\n') + '\n';
    }
    const allRows = [
        ...(pricingData.material_costs || []),
        ...(pricingData.labour_rates || []),
        ...(pricingData.equipment || []),
        ...(pricingData.subcontractors || []),
    ];
    const pendingCount = allRows.filter(r => r?.source === 'ai_discovered').length;
    if (pendingCount > 0) block += `Pending AI-discovered rates excluded until approved in Pricing Reviews: ${pendingCount}\n`;
    const estimateCount = allRows.filter(r => isVisiblePricingItem(r) && !isTrustedPricingItem(r)).length;
    if (estimateCount > 0) block += `AI Review estimate rates excluded until approved in Pricing: ${estimateCount}\n`;
    if (pricingData.administration?.quote_administration_fee) {
        block += `Administration Fee: $${pricingData.administration.quote_administration_fee}\n`;
    }
    if (pricingData._note) block += `(${pricingData._note})\n`;
    return block;
};

// D. Build tiered scope context for AI prompts - formats scope items with relevance hints
export const buildTieredScopeContext = (scopeItems, userMessage = '') => {
    if (!scopeItems || scopeItems.length === 0) return { text: '(No scope items yet)' };
    const msg = (userMessage || '').toLowerCase();
    const lines = scopeItems.map((item, idx) => {
        const amount = parseFloat((item.amount || '0').toString().replace(/,/g, '')) || 0;
        // Inclusions can be strings OR { type: 'heading', text: '...' } objects.
        // For the LLM context block, render headings inline as "[HEADING] EXTERNAL" so the
        // model still understands the grouping but never sees [object Object].
        const inclusions = (item.inclusions || [])
            .map(inc => {
                if (inc && typeof inc === 'object' && inc.type === 'heading') {
                    return `[HEADING] ${inc.text || ''}`;
                }
                return typeof inc === 'string' ? inc : '';
            })
            .filter(Boolean)
            .join(', ');
        const photoRefs = [
            ...(Array.isArray(item.images) ? item.images : []),
            ...(Array.isArray(item.photos) ? item.photos : []),
            ...(Array.isArray(item.assignedPhotos) ? item.assignedPhotos : []),
            ...(Array.isArray(item.selectedPhotos) ? item.selectedPhotos : []),
        ];
        const photos = photoRefs.length;
        let line = `[${idx + 1}] ${item.title || 'Untitled'}: $${amount.toLocaleString()}`;
        // The Scope Description the quote prints, not the internal method text.
        const wording = resolveClientScopeDescription(item, { fallbackToTitle: false });
        if (wording) line += ` - ${wording}`;
        if (inclusions) line += ` | Inclusions: ${inclusions}`;
        if (photos > 0) line += ` | ${photos} photo(s)`;
        return line;
    });
    return { text: lines.join('\n') };
};

// E. Build photo context for AI prompts - lists available photos with relevance to scope
export const buildPhotoContext = (photos, userMessage = '', scopeItems = []) => {
    if (!photos || photos.length === 0) return { text: '(No photos available)' };
    const lines = photos.map((photo, idx) => {
        const name = photo.aiLabel || photo.name || photo.originalName || `Photo ${idx + 1}`;
        const assigned = scopeItems.filter(s => {
            const refs = [
                ...(Array.isArray(s.images) ? s.images : []),
                ...(Array.isArray(s.photos) ? s.photos : []),
                ...(Array.isArray(s.assignedPhotos) ? s.assignedPhotos : []),
                ...(Array.isArray(s.selectedPhotos) ? s.selectedPhotos : []),
            ];
            return refs.some(p => p === idx || p?.index === idx || (photo.id && p?.id === photo.id));
        }).length;
        // 1-based, like every tool's photoIndex. The matching above keeps the
        // 0-based array position.
        let line = `[${idx + 1}] ${name}`;
        if (photo.aiLabel && photo.name !== photo.aiLabel) line += ` (file: ${photo.name})`;
        if (photo.fromVideo) line += ` [video frame${photo.timestamp ? ' @' + photo.timestamp + 's' : ''}]`;
        if (photo.ocrText) line += ` (has OCR text)`;
        if (isManuallyMarkedUp(photo)) line += ` (marked up)`;
        if (assigned > 0) line += ` - assigned to ${assigned} scope item(s)`;
        return line;
    });
    return { text: lines.join('\n') };
};

// Strip email noise (signatures, headers, disclaimers) from document text
export const stripEmailNoise = (text) => {
    if (!text) return '';
    text = text.replace(/^(From|To|Cc|Bcc|Date|Sent|Received|Subject|Importance|Reply-To|Message-ID|MIME-Version|Content-Type|X-[\w-]+):\s*[^\n]*\n/gim, '');
    const sigPatterns = [
        /\n--\s*\n[\s\S]*$/,
        /\n(Kind |Best |Warm )?[Rr]egards,?\n[\s\S]{0,500}$/,
        /\nSent from my (iPhone|iPad|Galaxy|Android|Outlook)[\s\S]*$/i,
        /\n_{3,}\n[\s\S]*$/,
    ];
    for (const pattern of sigPatterns) {
        const match = text.match(pattern);
        if (match && match.index > text.length * 0.3) {
            text = text.substring(0, match.index).trim();
        }
    }
    text = text.replace(/\n(IMPORTANT:|DISCLAIMER:|CONFIDENTIAL(?:ITY)?(?:\s+NOTICE)?:?)[\s\S]{100,1000}$/i, '');
    text = text.replace(/\n{3,}/g, '\n\n').replace(/[ \t]{3,}/g, ' ').trim();
    return text;
};

// Assemble complete document text for AI prompt context.
//
// The legacy maxPerDoc/maxTotal/budgets options remain in the signature so old
// callers do not break, but they no longer discard customer evidence. Provider
// context limits must fail explicitly at the model boundary; silently replacing
// the tail of an RFQ/PDF with a truncation marker can change scope authority.
export const condenseDocuments = (documents, _legacyBudgetOptions = {}) => {
    // Filter null/undefined entries that could crash the loop
    if (Array.isArray(documents)) documents = documents.filter(Boolean);
    if (!documents || documents.length === 0) return '';
    // Build index map BEFORE sorting so budget lookups reference the original array position
    const indexed = documents.map((doc, i) => ({ doc, originalIndex: i }));
    // The client's request and any scope of works come first, by the class
    // their text was given, never by their file names (CN6). The sort is
    // stable, so every other document keeps the caller's order.
    const leads = new Map(indexed.map(entry => [entry, isRequestOrScopeDocument(entry.doc) ? 0 : 1]));
    indexed.sort((a, b) => leads.get(a) - leads.get(b));
    let sections = [];
    for (const { doc, originalIndex } of indexed) {
        // CN5: each document is headed by its position in the list and its
        // format, never its file name ("file names shouldnt dictate anything.
        // only the contents."). The position is the document's place in the
        // list the caller passed, so it is the same whatever the order here.
        const label = aiDocumentLabel(originalIndex + 1, doc);
        // v5.40.0 (UX audit Wave-1 P0 #3): documents flagged as extraction-failed at
        // import time are surfaced to the AI as a known-missing input rather than
        // either (a) silently included with a placeholder sentinel as content, or
        // (b) silently omitted from the document inventory entirely. The AI then
        // knows "this PDF was attached but unreadable" and can ask the operator to
        // re-import it rather than pretending nothing was attached.
        if (doc.extractionFailed === true) {
            sections.push(`\n--- ${label} --- [Attached but extraction failed at import — content unavailable to the AI. Operator should re-import or extract manually.]`);
            continue;
        }
        const primaryContent = typeof doc.content === 'string' && doc.content.trim()
            ? doc.content
            : '';
        let content = primaryContent || (typeof doc.text === 'string' ? doc.text : '');
        if (!content || content.length < 30) {
            sections.push(`\n--- ${label} --- [No extractable content]`);
            continue;
        }
        content = stripEmailNoise(estimatorNotesPromptText(doc, content));
        const coverage = doc.contentComplete === false
            ? ' [INCOMPLETE EXTRACTION - do not claim the whole document was read]'
            : '';
        // D6: the business's own scope notes are labelled so no stage reads
        // them as the client's request.
        const role = estimatorNotesHeaderRole(doc);
        sections.push(`\n--- ${label}${role}${coverage} ---\n${content}`);
    }
    return '\n\nDOCUMENTS:\n' + sections.join('\n');
};

// Comprehensive KPI calculation (local, no API needed)
export const calculateComprehensiveKPI = ({ formData: fd, photos = [], videos = [], docs = [], riskAnalysis: ra, quoteData: qd }) => {
    const text = fd?.notes || '';
    const wordCount = text.split(/\s+/).filter(w => w.length > 0).length;
    const hasObs = /observ|found|noted|identified|detected/i.test(text);
    const hasScope = /scope|works|repair|replace|install/i.test(text);
    const hasSafety = /safety|hazard|risk|danger|ppe|access/i.test(text);
    const hasDefects = /defect|damage|crack|leak|fail/i.test(text);
    const hasLocation = /unit|level|floor|area|section|room/i.test(text);
    const hasMeasure = /\d+\s*(m2|m²|sqm|mm|metres|meters|linear|lm)\b/i.test(text);
    const hasPhotos = /photo|image|picture|refer|see attached/i.test(text);

    // Category 1: Field Documentation (30 weight)
    const docItems = [];
    const wcScore = wordCount >= 300 ? 10 : wordCount >= 150 ? 8 : wordCount >= 50 ? 5 : wordCount >= 20 ? 2 : 0;
    docItems.push({ label: 'Word count (300+ target)', value: wordCount + ' words', score: wcScore, max: 10, status: wcScore >= 8 ? 'pass' : wcScore >= 5 ? 'good' : 'fail' });
    docItems.push({ label: 'Observations noted', value: hasObs ? 'Yes' : 'Missing', score: hasObs ? 5 : 0, max: 5, status: hasObs ? 'pass' : 'fail' });
    docItems.push({ label: 'Scope described', value: hasScope ? 'Yes' : 'Missing', score: hasScope ? 5 : 0, max: 5, status: hasScope ? 'pass' : 'fail' });
    docItems.push({ label: 'Safety considerations', value: hasSafety ? 'Yes' : 'Missing', score: hasSafety ? 5 : 0, max: 5, status: hasSafety ? 'pass' : 'fail' });
    docItems.push({ label: 'Defects documented', value: hasDefects ? 'Yes' : 'Missing', score: hasDefects ? 5 : 0, max: 5, status: hasDefects ? 'pass' : 'fail' });
    docItems.push({ label: 'Locations specified', value: hasLocation ? 'Yes' : 'Missing', score: hasLocation ? 3 : 0, max: 3, status: hasLocation ? 'pass' : 'fail' });
    docItems.push({ label: 'Measurements included', value: hasMeasure ? 'Yes' : 'Missing', score: hasMeasure ? 4 : 0, max: 4, status: hasMeasure ? 'pass' : 'fail' });
    docItems.push({ label: 'Photo references in notes', value: hasPhotos ? 'Yes' : 'Missing', score: hasPhotos ? 3 : 0, max: 3, status: hasPhotos ? 'pass' : 'fail' });
    const docTotal = docItems.reduce((s, i) => s + i.score, 0);
    const docMax = docItems.reduce((s, i) => s + i.max, 0);

    // Category 2: Site Evidence (25 weight)
    const evItems = [];
    const photoCount = (photos || []).length;
    const videoCount = (videos || []).length;
    const docCount = (docs || []).length;
    const photoScore = photoCount >= 8 ? 10 : photoCount >= 4 ? 7 : photoCount >= 1 ? 4 : 0;
    const videoScore = videoCount >= 2 ? 10 : videoCount >= 1 ? 6 : 0;
    const docScore = docCount >= 2 ? 5 : docCount >= 1 ? 3 : 0;
    evItems.push({ label: 'Photos captured', value: photoCount + ' photo' + (photoCount !== 1 ? 's' : ''), score: photoScore, max: 10, status: photoScore >= 7 ? 'pass' : photoScore >= 4 ? 'good' : 'fail' });
    evItems.push({ label: 'Videos recorded', value: videoCount + ' video' + (videoCount !== 1 ? 's' : ''), score: videoScore, max: 10, status: videoScore >= 6 ? 'pass' : videoScore >= 1 ? 'good' : 'fail' });
    evItems.push({ label: 'Documents attached', value: docCount + ' doc' + (docCount !== 1 ? 's' : ''), score: docScore, max: 5, status: docScore >= 3 ? 'pass' : docScore >= 1 ? 'good' : 'fail' });
    const evTotal = evItems.reduce((s, i) => s + i.score, 0);
    const evMax = evItems.reduce((s, i) => s + i.max, 0);

    // Category 3: Data Completeness (20 weight)
    const dcItems = [];
    const check = (label, val, max) => {
        const filled = val && val.toString().trim().length > 0;
        return { label, value: filled ? 'Provided' : 'Missing', score: filled ? max : 0, max, status: filled ? 'pass' : 'fail' };
    };
    dcItems.push(check('Client name', fd?.clientName, 3));
    dcItems.push(check('Client company', fd?.clientCompany, 3));
    dcItems.push(check('Client email', fd?.clientEmail, 2));
    dcItems.push(check('Client phone', fd?.clientPhone, 2));
    dcItems.push(check('Strata plan', fd?.strataplan, 3));
    dcItems.push(check('Unit number', fd?.unitNumber, 2));
    dcItems.push(check('Address', fd?.address, 3));
    dcItems.push(check('Visit date', fd?.visitDate, 2));
    dcItems.push(check('Order / WO number', fd?.orderNumber, 2));
    dcItems.push(check('Inspector name', fd?.assessor, 2));
    const dcTotal = dcItems.reduce((s, i) => s + i.score, 0);
    const dcMax = dcItems.reduce((s, i) => s + i.max, 0);

    // Category 4: Quote Quality (25 weight)
    const qqItems = [];
    const scopeItems = qd?.scope || [];
    const scopeCount = scopeItems.length;
    const scopeScore = scopeCount >= 5 ? 10 : scopeCount >= 3 ? 7 : scopeCount >= 1 ? 4 : 0;
    qqItems.push({ label: 'Scope items generated', value: scopeCount + ' item' + (scopeCount !== 1 ? 's' : ''), score: scopeScore, max: 10, status: scopeScore >= 7 ? 'pass' : scopeScore >= 4 ? 'good' : 'fail' });

    const riskConf = ra?.confidence_score || 0;
    const riskScore = riskConf >= 80 ? 10 : riskConf >= 60 ? 7 : riskConf >= 40 ? 4 : riskConf > 0 ? 2 : 0;
    qqItems.push({ label: 'Risk analysis confidence', value: riskConf > 0 ? riskConf + '%' : 'Not run', score: riskScore, max: 10, status: riskScore >= 7 ? 'pass' : riskScore >= 4 ? 'good' : riskConf > 0 ? 'good' : 'fail' });

    const standards = ra?.detected_standards || ra?.standards || [];
    const stdCount = Array.isArray(standards) ? standards.length : 0;
    const stdScore = stdCount >= 3 ? 5 : stdCount >= 1 ? 3 : 0;
    qqItems.push({ label: 'Standards identified', value: stdCount > 0 ? stdCount + ' standard' + (stdCount !== 1 ? 's' : '') : 'None', score: stdScore, max: 5, status: stdScore >= 3 ? 'pass' : stdScore >= 1 ? 'good' : 'fail' });

    const qqTotal = qqItems.reduce((s, i) => s + i.score, 0);
    const qqMax = qqItems.reduce((s, i) => s + i.max, 0);

    // Build categories
    const categories = [
        { name: 'Field Documentation', icon: 'FileText', color: '#3b82f6', weight: 30, items: docItems, rawScore: docTotal, rawMax: docMax, score: Math.round((docTotal / docMax) * 100), weighted: Math.round((docTotal / docMax) * 30) },
        { name: 'Site Evidence', icon: 'Camera', color: '#8b5cf6', weight: 25, items: evItems, rawScore: evTotal, rawMax: evMax, score: Math.round((evTotal / evMax) * 100), weighted: Math.round((evTotal / evMax) * 25) },
        { name: 'Data Completeness', icon: 'ClipboardCheck', color: '#f59e0b', weight: 20, items: dcItems, rawScore: dcTotal, rawMax: dcMax, score: Math.round((dcTotal / dcMax) * 100), weighted: Math.round((dcTotal / dcMax) * 20) },
        { name: 'Quote Quality', icon: 'Award', color: '#10b981', weight: 25, items: qqItems, rawScore: qqTotal, rawMax: qqMax, score: Math.round((qqTotal / qqMax) * 100), weighted: Math.round((qqTotal / qqMax) * 25) }
    ];

    const overallScore = categories.reduce((s, c) => s + c.weighted, 0);
    const grade = overallScore >= 95 ? 'A+' : overallScore >= 85 ? 'A' : overallScore >= 78 ? 'B+' : overallScore >= 70 ? 'B' : overallScore >= 62 ? 'C+' : overallScore >= 50 ? 'C' : overallScore >= 35 ? 'D' : 'F';

    // Generate strengths/improvements
    const strengths = [];
    const improvements = [];
    if (wordCount >= 150) strengths.push('Detailed field notes');
    if (photoCount >= 4) strengths.push('Good photographic evidence');
    if (videoCount >= 1) strengths.push('Video documentation included');
    if (hasDefects && hasObs) strengths.push('Thorough defect observations');
    if (hasSafety) strengths.push('Safety considerations addressed');
    if (hasMeasure) strengths.push('Measurements included in notes');
    if (dcTotal / dcMax >= 0.8) strengths.push('Client data well documented');
    if (scopeCount >= 5) strengths.push('Comprehensive scope of works');

    if (wordCount < 150) improvements.push('Add more detail to field notes (target 300+ words)');
    if (!hasSafety) improvements.push('Include safety considerations and hazards');
    if (!hasMeasure) improvements.push('Add measurements and dimensions');
    if (!hasPhotos) improvements.push('Reference photos in note descriptions');
    if (photoCount < 4) improvements.push('Capture more site photos (target 8+)');
    if (docCount === 0) improvements.push('Attach supporting documents (reports, plans)');
    if (!fd?.clientEmail) improvements.push('Record client email address');
    if (!fd?.orderNumber) improvements.push('Include order/WO reference number');

    return {
        overallScore, grade, categories,
        strengths: strengths.slice(0, 5),
        improvements: improvements.slice(0, 5),
        assessor: fd?.assessor || 'Unknown',
        date: new Date().toISOString(),
        quoteRef: qd?.header?.title || '',
        photoCount, videoCount, docCount, scopeCount,
        wordCount
    };
};

// PDF TEXT EXTRACTION - multiple strategies for maximum reliability
//
// `textOnly: true` stops after the two text-layer strategies: a PDF with no
// text layer comes back as `{ success: false, needsOcr: true, text: '' }`
// without its pages being rendered to images. For callers that only need to
// know what a document says, such as the Training Studio's inventory, which
// reads every document in a folder and shows the ones it cannot read.
//
// `onPage(done, total)` is told after each page of a text-layer strategy, for
// a progress bar. A callback that throws never stops the read.
/**
 * @param {any} file
 * @param {{ textOnly?: boolean, onPage?: ((done: number, total: number) => void) | null }} [opts]
 */
export async function extractPdfText(file, { textOnly = false, onPage = null } = {}) {
    const pdfjsLib = await loadPdfjs();
    const reportPage = (done, total) => {
        try { if (typeof onPage === 'function') onPage(done, total); } catch { /* progress only */ }
    };

    // Strategy 1: pdf.js with configured worker
    let strategyOnePdf = null;
    try {
        const pdf = await loadPdfDocument(pdfjsLib, file);
        strategyOnePdf = pdf;
        let fullText = '';
        const pageEvidence = [];
        // Traverse the complete document. A previous 120-page ceiling was a
        // product limit rather than a pdf.js/model capability and silently
        // omitted the tail of long specifications.
        for (let i = 1; i <= pdf.numPages; i++) {
            const page = await pdf.getPage(i);
            const content = await page.getTextContent();
            const viewport = page.getViewport({ scale: 1 });
            const ordered = orderPdfTextItems(content.items, viewport);
            const pageText = ordered.text;
            pageEvidence.push({
                page: i,
                width: Math.round(viewport.width * 100) / 100,
                height: Math.round(viewport.height * 100) / 100,
                rotation: viewport.rotation || 0,
                extractedCharacters: pageText.length,
                layout: ordered.layout,
            });
            if (pageText) fullText += `\n--- Page ${i} ---\n${pageText}\n`;
            reportPage(i, pdf.numPages);
            await yieldToMain();
        }
        const trimmed = fullText.trim();
        if (trimmed.length > 20) {
            console.log(`[PDF] Extracted ${trimmed.length} chars from ${pdf.numPages} pages (worker strategy)`);
            return {
                text: trimmed,
                success: true,
                pages: pdf.numPages,
                processedPages: pageEvidence.length,
                contentComplete: pageEvidence.length === pdf.numPages,
                contentTruncated: pageEvidence.length !== pdf.numPages,
                pageEvidence,
            };
        }
        // If we got very little text, the PDF may be image-based - fall through to Strategy 2
        console.warn('[PDF] Worker extraction returned minimal text, trying no-worker fallback...');
    } catch (err) {
        console.warn('[PDF] Worker strategy failed:', err.message, '- trying no-worker fallback...');
    } finally {
        await destroyPdfDocument(strategyOnePdf, 'PDF worker strategy');
    }

    // Strategy 2: pdf.js with relaxed parsing options (runs WITH the configured worker
    // but uses more permissive options to handle malformed/encrypted/edited PDFs that
    // the strict default parser rejects). Common cause of failure: PDFs that have been
    // re-saved from an editor (e.g. "revised edition" PDFs) often have non-standard
    // structures that strict parsing rejects but canvas rendering can still handle.
    //
    // FIXED: previously this branch tried to set workerSrc='' to disable the worker
    // but pdf.js v3+ throws "No GlobalWorkerOptions.workerSrc specified" when the
    // workerSrc is empty. Now we use the legacy disableWorker:true option which
    // actually runs pdf.js on the main thread without throwing.
    let strategyTwoPdf = null;
    try {
        const pdf = await loadPdfDocument(pdfjsLib, file, {
            disableWorker: true,        // Run on main thread - bypasses worker config issues
            useWorkerFetch: false,
            isEvalSupported: false,
            useSystemFonts: true,
            stopAtErrors: false,        // Don't bail on the first malformed object
            disableFontFace: true,      // Skip font loading (often the failure point)
            disableAutoFetch: true,     // Don't try to fetch additional resources
            disableStream: true,        // Disable streaming (we have the full buffer)
        });
        strategyTwoPdf = pdf;
        let fullText = '';
        const pageEvidence = [];
        const failedPages = [];
        // The relaxed fallback is slower, but it must remain a complete
        // traversal. Its former 60-page ceiling could turn a partially read
        // contract into an apparently successful extraction.
        for (let i = 1; i <= pdf.numPages; i++) {
            try {
                const page = await pdf.getPage(i);
                const content = await page.getTextContent();
                const viewport = page.getViewport({ scale: 1 });
                const ordered = orderPdfTextItems(content.items, viewport);
                const pageText = ordered.text;
                pageEvidence.push({
                    page: i,
                    width: Math.round(viewport.width * 100) / 100,
                    height: Math.round(viewport.height * 100) / 100,
                    rotation: viewport.rotation || 0,
                    extractedCharacters: pageText.length,
                    layout: ordered.layout,
                });
                if (pageText) fullText += `\n--- Page ${i} ---\n${pageText}\n`;
            } catch (pageErr) {
                failedPages.push(i);
                console.warn(`[PDF] Page ${i} extraction failed in fallback:`, pageErr.message);
            }
            reportPage(i, pdf.numPages);
            await yieldToMain();
        }
        const trimmed = fullText.trim();
        if (trimmed.length > 20) {
            console.log(`[PDF] Extracted ${trimmed.length} chars (relaxed-options fallback)`);
            return {
                text: trimmed,
                success: true,
                pages: pdf.numPages,
                processedPages: pageEvidence.length,
                failedPages,
                contentComplete: pageEvidence.length === pdf.numPages && failedPages.length === 0,
                contentTruncated: pageEvidence.length !== pdf.numPages || failedPages.length > 0,
                pageEvidence,
            };
        }
        console.warn('[PDF] Relaxed-options fallback returned minimal text - PDF may be image-based');
    } catch (err) {
        console.warn('[PDF] Relaxed-options fallback failed:', err.message);
    } finally {
        await destroyPdfDocument(strategyTwoPdf, 'PDF relaxed strategy');
    }

    if (textOnly) {
        return { text: '', success: false, needsOcr: true, textOnly: true, images: [], contentComplete: false, contentTruncated: true };
    }

    // Strategy 3: Render PDF pages to canvas images for AI vision OCR
    // Returns the images so the caller can send them to the AI vision model
    let strategyThreePdf = null;
    try {
        console.log('[PDF] Attempting canvas render for OCR fallback...');
        const pdf = await loadPdfDocument(pdfjsLib, file);
        strategyThreePdf = pdf;
        const images = [];
        const failedPages = [];
        // Each page's size in PDF units. A page with no text layer is judged
        // a drawing or not by its size alone (CN3: never by the file name).
        const pageGeometry = [];

        // Render every page sequentially. Dimension capping + yielding bound
        // browser memory/CPU without imposing a content/page ceiling.
        for (let i = 1; i <= pdf.numPages; i++) {
            let canvas = null;
            try {
                const page = await pdf.getPage(i);
                // Cap the OCR render dimension — an A0/A1 image-based PDF otherwise
                // builds a canvas large enough to starve the main thread. Normal pages
                // keep their 1.5× quality (effectiveScale stays at 1.5).
                const ocrBase = page.getViewport({ scale: 1 });
                pageGeometry.push({
                    page: i,
                    width: Math.round(ocrBase.width * 100) / 100,
                    height: Math.round(ocrBase.height * 100) / 100,
                });
                const ocrMaxEdge = Math.max(ocrBase.width, ocrBase.height) || 1;
                const ocrScale = Math.min(1.5, MAX_RENDER_DIM / ocrMaxEdge) || 1.5;
                const viewport = page.getViewport({ scale: ocrScale }); // Good quality for OCR
                canvas = document.createElement('canvas');
                canvas.width = viewport.width;
                canvas.height = viewport.height;
                const ctx = canvas.getContext('2d');
                await page.render({ canvasContext: ctx, viewport, intent: PDF_EXTRACTION_RENDER_INTENT }).promise;
                const dataUrl = await canvasToJpegDataUrl(canvas, 0.85);
                images.push({ page: i, data: dataUrl });
            } catch (pageErr) {
                failedPages.push(i);
                console.warn(`[PDF] Page ${i} canvas render failed:`, pageErr?.message || pageErr);
            } finally {
                if (canvas) {
                    canvas.width = 0;
                    canvas.height = 0;
                }
            }
            await yieldToMain();
        }

        if (images.length > 0) {
            console.log(`[PDF] Rendered ${images.length} pages as images for OCR`);
            return {
                text: `[PDF contains ${pdf.numPages} page(s) of image-based content. ${images.length} page(s) rendered for AI OCR.]`,
                success: false,
                needsOcr: true,
                images,
                pageGeometry,
                pages: pdf.numPages,
                processedPages: images.length,
                failedPages,
                renderCoverage: {
                    schemaVersion: 1,
                    status: images.length === pdf.numPages && failedPages.length === 0 ? 'complete' : 'partial',
                    totalPages: pdf.numPages,
                    renderedPages: images.map(image => image.page),
                    failedPages,
                },
                contentComplete: false,
                contentTruncated: true,
            };
        }
    } catch (err) {
        console.error('[PDF] Canvas render fallback failed:', err.message);
    } finally {
        await destroyPdfDocument(strategyThreePdf, 'PDF canvas strategy');
    }

    // Strategy 4: If pdf.js completely fails, convert PDF file to data URL
    // and let the caller send it to AI vision as a raw document image
    try {
        if (shouldUsePdfRangeTransport(file)) {
            throw new Error('raw base64 fallback disabled for range-routed PDF');
        }
        console.log('[PDF] All pdf.js strategies failed. Providing raw file for AI vision fallback...');
        const dataUrl = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => reject(new Error('FileReader failed'));
            reader.readAsDataURL(file);
        });
        return {
            text: `[PDF could not be parsed by pdf.js. Raw file provided for AI vision OCR.]`,
            success: false,
            needsOcr: true,
            images: [{ page: 1, data: dataUrl }],
            pages: null,
            processedPages: 1,
            contentComplete: false,
            contentTruncated: true,
            rawPdf: true
        };
    } catch (err) {
        console.error('[PDF] Raw file fallback also failed:', err.message);
    }

    return {
        // CN5: this text is stored as the document's content and can reach a
        // prompt, so it names no file.
        text: '[PDF extraction failed - could not read text or render pages from this PDF]',
        success: false,
        contentComplete: false,
        contentTruncated: true,
    };
}

/**
 * True for the explanatory text extractPdfText returns INSTEAD of document content (success:false): the image-based placeholder, the
 * unparseable-file placeholder and the terminal "extraction failed" placeholder. It is a message about the file, never evidence from it.
 * A preprocessed row kept by an earlier build may still carry one as its `text`.
 */
export function isPdfExtractionPlaceholderText(text) {
    return /^\s*\[PDF (?:extraction failed|contains \d+ page|could not be parsed)/.test(String(text || ''));
}

/**
 * Render PDF pages to high-quality images for vision AI analysis.
 * Uses pdf.js canvas rendering - same approach as extractPdfText's OCR
 * fallback but with configurable scale and quality.
 *
 * @param {File} file - PDF file to render
 * @param {Object} [opts]
 * @param {number} [opts.scale=2.0] - Render scale (2.0 = 2x resolution)
 * @param {number} [opts.quality=0.90] - JPEG quality (0-1)
 * @param {number[]} [opts.pageNumbers] - optional 1-based page numbers to render
 * @param {boolean} [opts.withCoverage=false] - Return images plus an exact traversal receipt
 * @param {(done:number, total:number) => void} [opts.onPage] - Told after each page, for a progress bar; a throw is ignored
 * @returns {Promise<Array<{data:string, page:number}>|{images:Array,coverage:Object}>} Rendered page images, optionally with coverage
 */
export async function renderPdfToImages(file, { scale = 2.0, quality = 0.90, pageNumbers = null, withCoverage = false, onPage = null } = {}) {
    if (!file) {
        const emptyCoverage = {
            schemaVersion: 1,
            status: 'failed',
            totalPages: null,
            requestedPages: [],
            renderedPages: [],
            failedPages: [],
            error: 'No PDF file supplied',
        };
        const images = attachPdfCoverage([], emptyCoverage);
        return withCoverage ? { images, coverage: emptyCoverage } : images;
    }
    const pdfjsLib = await loadPdfjs();
    const pdf = await loadPdfDocument(pdfjsLib, file);
    const totalPages = pdf.numPages;
    const selectedPages = Array.isArray(pageNumbers) && pageNumbers.length > 0
        ? [...new Set(pageNumbers.map(Number).filter(page => Number.isInteger(page) && page >= 1 && page <= totalPages))]
        : Array.from({ length: totalPages }, (_, index) => index + 1);
    const images = [];
    const failedPages = [];

    try {
        for (const pageNumber of selectedPages) {
            let canvas = null;
            try {
                const page = await pdf.getPage(pageNumber);
                // Cap the render dimension so a huge page (A1/A0 plan) doesn't build a
                // canvas large enough to starve the main thread / OOM. Normal A4/A3 pages
                // are unaffected (effectiveScale stays at the requested `scale`).
                const base = page.getViewport({ scale: 1 });
                const maxEdge = Math.max(base.width, base.height) || 1;
                const effectiveScale = Math.min(scale, MAX_RENDER_DIM / maxEdge) || scale;
                const viewport = page.getViewport({ scale: effectiveScale });
                canvas = document.createElement('canvas');
                canvas.width = viewport.width;
                canvas.height = viewport.height;
                const ctx = canvas.getContext('2d');
                await page.render({ canvasContext: ctx, viewport, intent: PDF_EXTRACTION_RENDER_INTENT }).promise;
                // Async (non-blocking) JPEG encode — toDataURL would freeze the tab.
                const dataUrl = await canvasToJpegDataUrl(canvas, quality);
                images.push({ data: dataUrl, page: pageNumber });
            } catch (pageErr) {
                failedPages.push(pageNumber);
                console.warn(`[PDF] Page ${pageNumber} render failed:`, pageErr?.message || pageErr);
            } finally {
                if (canvas) {
                    canvas.width = 0;
                    canvas.height = 0;
                }
            }
            try { if (typeof onPage === 'function') onPage(images.length + failedPages.length, selectedPages.length); } catch { /* progress only */ }
            // Breathe between pages so a multi-page render stays responsive.
            await yieldToMain();
        }
    } finally {
        try {
            if (typeof pdf.destroy === 'function') await pdf.destroy();
        } catch (destroyErr) {
            console.warn('[PDF] Render document cleanup failed:', destroyErr?.message || destroyErr);
        }
    }

    const renderedPages = images.map(image => image.page);
    const requestedEveryPage = selectedPages.length === totalPages
        && selectedPages.every((page, index) => page === index + 1);
    const coverage = {
        schemaVersion: 1,
        status: requestedEveryPage && failedPages.length === 0 && renderedPages.length === totalPages
            ? 'complete'
            : (renderedPages.length > 0 ? 'partial' : 'failed'),
        totalPages,
        requestedPages: selectedPages,
        renderedPages,
        failedPages,
    };
    attachPdfCoverage(images, coverage);
    return withCoverage ? { images, coverage } : images;
}

/**
 * Extract EMBEDDED images (XObjects) from a PDF. This is different from
 * renderPdfToImages: that renders entire pages as images. This function walks
 * the pdfjs operator stream on each page, finds paintImageXObject calls, and
 * extracts the underlying embedded image (typically JPEG/PNG resources stored
 * inside the PDF — e.g., site photos embedded in an inspection report).
 *
 * The point: a dilapidation report with 30 site photos and surrounding prose
 * was previously losing all visual content because text extraction succeeded
 * (so the rendered-pages fallback never fired) but the embedded photos stayed
 * trapped inside the PDF.
 *
 * Returned images are transient document derivatives. Stage 1 consumes them
 * through the PDF document-visual evidence lane and persists only grounded
 * text/receipts; they must not be promoted into project photos as if the
 * customer independently uploaded them.
 *
 * Filters:
 *   - Dedupes by image XObject name so the same logo on every page only
 *     emits once.
 *   - Traverses every page and returns every eligible unique image. Output
 *     dimensions remain bounded so a single decoded photo cannot exhaust the
 *     browser, but there is no first-N page/image ceiling.
 *
 * Result shape: Array<{ page, imageId, dataUrl, width, height, mimeType }>.
 *
 * Failure mode: returns [] on error for legacy callers. Coverage-aware callers
 * receive an exact failed/partial receipt and MUST either recover the listed
 * pages with bounded whole-page renders or stop the import. Every unresolved
 * image is logged with its reason, so "no photos came back" is never silently
 * mistaken for complete evidence.
 *
 * @param {File}   file
 * @param {Object} [opts]
 * @param {number} [opts.quality=0.85]         JPEG quality for the canvas re-encode
 * @param {number} [opts.maxEdge=1280]         Longest edge of the returned image
 * @param {number} [opts.resolveTimeoutMs=1500] Per-image give-up window for the pdfjs object resolve
 * @param {boolean} [opts.withCoverage=false]  Return images plus an exact traversal receipt
 */
export async function extractEmbeddedImagesFromPdf(file, opts = {}) {
    const {
        quality = 0.85,
        // Bound the OUTPUT, not just the input. This used to re-encode at the
        // embed's native resolution, so a 12 MP photo inside an RFQ came back as
        // multiple megabytes of base64 — and Stage 1 pushes these straight into
        // photos[] without passing them through ImageCompressionService. Six of
        // them in one photo-forensic batch is over 30 MB, which is BOTH our own
        // AI-proxy cap and OpenRouter's, so the batch failed, coverage came back
        // incomplete, and quote generation stopped (2026-08-18).
        //
        // The cap was invisible for months because this function returned zero
        // images on pdfjs 4.x (it checked for `undefined` where the library
        // returns `null`). Fixing that turned a dormant sizing bug live.
        //
        // 1280 is the same longest edge ImageCompressionService.MAX_EDGE gives
        // every other photo in the app, so an embedded photo now costs what a
        // camera photo costs, and the forensic model sees the same detail it has
        // always seen on the canonical path.
        maxEdge = 1280,
        resolveTimeoutMs = 1500,
        withCoverage = false,
    } = opts;
    const finish = (images, coverage) => {
        attachPdfCoverage(images, coverage);
        return withCoverage ? { images, coverage } : images;
    };
    if (!file) {
        return finish([], {
            schemaVersion: 1,
            status: 'failed',
            totalPages: null,
            visitedPages: [],
            failedPages: [],
            incompletePages: [],
            imageDrawsSeen: 0,
            uniqueImagesAttempted: 0,
            imagesExtracted: 0,
            pageRenderFallbackRequiredPages: [],
            error: 'No PDF file supplied',
        });
    }
    let pdfjsLib;
    let pdf;
    try {
        pdfjsLib = await loadPdfjs();
        pdf = await loadPdfDocument(pdfjsLib, file);
    } catch (err) {
        console.warn('[PDF embed] Document load failed:', err?.message || err);
        return finish([], {
            schemaVersion: 1,
            status: 'failed',
            totalPages: null,
            visitedPages: [],
            failedPages: [],
            incompletePages: [],
            imageDrawsSeen: 0,
            uniqueImagesAttempted: 0,
            imagesExtracted: 0,
            pageRenderFallbackRequiredPages: [],
            error: err?.message || String(err),
        });
    }

    // A PDF document holds its decoded page bitmaps until it is destroyed, so a
    // 400 KB report with nine photos can pin tens of MB after we are finished
    // with it. Release it on every exit path, including the early returns.
    // See gotcha_photo_heavy_render_decode_freeze.
    const releaseDocument = async () => {
        try {
            if (pdf && typeof pdf.destroy === 'function') await pdf.destroy();
        } catch (err) {
            console.warn('[PDF embed] document destroy failed:', err?.message || err);
        }
    };

    /**
     * Pick the registry that holds an image XObject.
     *
     * WHY: pdfjs keeps per-page objects on `page.objs` but promotes objects it
     * caches for the whole document (a logo repeated on every page, any image
     * the global image cache adopts) onto `page.commonObjs`, giving them an id
     * prefixed with the document id, which always begins "g_". Asking the wrong
     * registry does not throw: `get` creates an empty placeholder that never
     * resolves, so every promoted image used to be lost to the defensive
     * timeout. Prefer whichever registry already holds the id and fall back to
     * the "g_" prefix convention when neither has it yet.
     */
    const pickStore = (page, name) => {
        const common = page.commonObjs;
        const local = page.objs;
        if (common && typeof common.has === 'function' && common.has(name)) return common;
        if (local && typeof local.has === 'function' && local.has(name)) return local;
        if (name.startsWith('g_') && common) return common;
        return local;
    };

    /**
     * Resolve one image XObject, reporting HOW it resolved so a give-up is
     * never mistaken for an empty image.
     *
     * WHY the callback is the only trusted signal: pdfjs 4.x
     * `objs.get(name, callback)` ALWAYS returns null when a callback is
     * supplied and delivers the data through that callback. The previous guard
     * here was `sync !== undefined`, which null satisfies, so the promise
     * settled with null before pdfjs had resolved anything and the caller
     * skipped every image. That is why this helper returned zero images for a
     * real quote containing nine. Only a NON-NULL synchronous return counts as
     * a resolve; everything else waits for the callback.
     *
     * @returns {Promise<{ data: any, reason: string }>}
     */
    const resolveImageObject = (store, name) => new Promise((resolve) => {
        let settled = false;
        let timer = null;
        const finish = (data, reason) => {
            if (settled) return;
            settled = true;
            // Clear the defensive timer so a real resolve is never overwritten
            // by a later give-up, and so we do not hold the closure alive.
            if (timer !== null) clearTimeout(timer);
            resolve({ data: data ?? null, reason });
        };
        let sync;
        try {
            sync = store.get(name, (data) => finish(data, 'callback'));
        } catch (err) {
            finish(null, `objs.get threw: ${err?.message || err}`);
            return;
        }
        if (sync != null) { finish(sync, 'sync'); return; }
        if (settled) return;
        // Some ids are referenced but never sent (soft masks, images the worker
        // failed to decode). Without this the walk would hang on them.
        timer = setTimeout(() => finish(null, `no resolve within ${resolveTimeoutMs}ms`), resolveTimeoutMs);
    });

    const OPS = pdfjsLib.OPS || {};
    const PAINT_OPS = new Set([
        OPS.paintImageXObject,
        OPS.paintImageXObjectRepeat,
    ].filter(v => v !== undefined));
    if (PAINT_OPS.size === 0) {
        console.warn('[PDF embed] pdfjs OPS constants not available; cannot walk images');
        await releaseDocument();
        return finish([], {
            schemaVersion: 1,
            status: 'failed',
            totalPages: pdf.numPages || 0,
            visitedPages: [],
            failedPages: Array.from({ length: pdf.numPages || 0 }, (_, index) => index + 1),
            incompletePages: Array.from({ length: pdf.numPages || 0 }, (_, index) => index + 1),
            imageDrawsSeen: 0,
            uniqueImagesAttempted: 0,
            imagesExtracted: 0,
            pageRenderFallbackRequiredPages: [],
            error: 'pdfjs image operator constants unavailable',
        });
    }

    const pageCount = pdf.numPages || 0;
    const attempted = new Set();
    const out = [];
    const visitedPages = [];
    const failedPages = new Set();
    const incompletePages = new Set();
    const pageRenderFallbackRequiredPages = new Set();
    // Counters so a zero-image result can be explained. "This PDF has no
    // embedded photos" and "it had nine and every one was discarded" must not
    // look the same in the console.
    let imageOpsSeen = 0;
    let unresolved = 0;
    let belowMinDimension = 0;
    let abovePixelCap = 0;
    let encodeFailed = 0;

    try {
        for (let pageNum = 1; pageNum <= pageCount; pageNum++) {
            let page;
            try {
                page = await pdf.getPage(pageNum);
            } catch (err) {
                failedPages.add(pageNum);
                incompletePages.add(pageNum);
                console.warn(`[PDF embed] page ${pageNum} load failed:`, err?.message || err);
                continue;
            }

            try {
                let opList;
                try {
                    opList = await page.getOperatorList();
                } catch (err) {
                    // Some malformed PDFs throw; skip this page and continue.
                    failedPages.add(pageNum);
                    incompletePages.add(pageNum);
                    console.warn(`[PDF embed] page ${pageNum} operator-list failed:`, err?.message || err);
                    continue;
                }
                visitedPages.push(pageNum);

                // Building the operator list is what makes pdfjs decode the page's
                // image XObjects and post them to the main thread, so by the time we
                // walk fnArray the objects are either present or on their way.
                const fnArray = opList.fnArray || [];
                const argsArray = opList.argsArray || [];
                const imageNames = [];
                for (let i = 0; i < fnArray.length; i++) {
                    if (!PAINT_OPS.has(fnArray[i])) continue;
                    const args = argsArray[i];
                    const name = Array.isArray(args) ? args[0] : null;
                    if (typeof name !== 'string') continue;
                    imageOpsSeen++;
                    // `attempted` is marked BEFORE the resolve, so an id that
                    // times out once is not re-tried on every later page.
                    if (attempted.has(name) || imageNames.includes(name)) continue;
                    imageNames.push(name);
                }
                if (imageNames.length === 0) {
                    await yieldToMain();
                    continue;
                }

                for (const name of imageNames) {
                    // Breathe between images so a page with several large embeds doesn't
                    // stack their (synchronous) RGBA-conversion blocks into a freeze.
                    await yieldToMain();
                    attempted.add(name);

                    const { data: imgObj, reason } = await resolveImageObject(pickStore(page, name), name);
                    if (!imgObj) {
                        unresolved++;
                        incompletePages.add(pageNum);
                        console.warn(`[PDF embed] page ${pageNum}: image "${name}" not recovered (${reason})`);
                        continue;
                    }

                    // Two shapes pdfjs returns for image XObjects:
                    //   A) { width, height, data, kind } raw decoded pixels
                    //      kind: 1=GRAYSCALE_1BPP, 2=RGB_24BPP, 3=RGBA_32BPP
                    //   B) { width, height, bitmap } ImageBitmap (preferred when
                    //      the browser supports OffscreenCanvas / createImageBitmap)
                    //   C) the object IS a HTMLImageElement-like with width/height
                    const width = imgObj.width || (imgObj.bitmap && imgObj.bitmap.width) || 0;
                    const height = imgObj.height || (imgObj.bitmap && imgObj.bitmap.height) || 0;
                    // Route monster raw embeds to a bounded whole-page render.
                    // _imgKindToRgba is an un-yieldable per-pixel loop; a full-page
                    // scan (e.g. 7000x9900, about 70 MP) can freeze the tab. The
                    // exact page number is returned in the coverage receipt and
                    // must be recovered by the caller before it publishes the PDF.
                    const hasBitmap = imgObj.bitmap && typeof imgObj.bitmap.width === 'number';
                    if (width * height > RAW_EMBED_MAIN_THREAD_DECODE_PIXELS && !hasBitmap) {
                        abovePixelCap++;
                        incompletePages.add(pageNum);
                        pageRenderFallbackRequiredPages.add(pageNum);
                        console.warn(`[PDF embed] deferring ${name} on page ${pageNum} to bounded page-render recovery: ${width}x${height} (~${Math.round(width * height / 1e6)} MP) exceeds the ${Math.round(RAW_EMBED_MAIN_THREAD_DECODE_PIXELS / 1e6)} MP direct raw-decode boundary`);
                        continue;
                    }

                    const canvas = document.createElement('canvas');
                    const longestEdge = Math.max(width, height);
                    const scale = maxEdge > 0 && longestEdge > maxEdge ? maxEdge / longestEdge : 1;
                    const outWidth = Math.max(1, Math.round(width * scale));
                    const outHeight = Math.max(1, Math.round(height * scale));
                    // ImageBitmap can be drawn directly to the bounded derivative.
                    // Raw pixels need their native canvas for putImageData, but the
                    // routing boundary above prevents pathological allocations.
                    canvas.width = hasBitmap ? outWidth : width;
                    canvas.height = hasBitmap ? outHeight : height;
                    const ctx = canvas.getContext('2d');

                    try {
                        if (hasBitmap) {
                            ctx.drawImage(imgObj.bitmap, 0, 0, outWidth, outHeight);
                        } else if (imgObj.data && imgObj.kind != null) {
                            // Raw pixel path, convert to RGBA ImageData
                            const rgba = _imgKindToRgba(imgObj);
                            if (!rgba) {
                                encodeFailed++;
                                incompletePages.add(pageNum);
                                console.warn(`[PDF embed] unsupported pixel kind ${imgObj.kind} for ${name}`);
                                canvas.width = 0; canvas.height = 0;
                                continue;
                            }
                            const imageData = new ImageData(rgba, width, height);
                            ctx.putImageData(imageData, 0, 0);
                        } else {
                            encodeFailed++;
                            incompletePages.add(pageNum);
                            console.warn(`[PDF embed] ${name} resolved to an unrecognised image shape; skipped`);
                            canvas.width = 0; canvas.height = 0;
                            continue;
                        }

                        // Downscale before encoding. The decode canvas has to be
                        // native size (putImageData will not scale), so the
                        // resize is a single drawImage onto a smaller canvas —
                        // no per-pixel JS, and the big canvas is released below
                        // either way.
                        let encodeCanvas = canvas;
                        let scaled = null;
                        if (!hasBitmap && scale < 1) {
                            scaled = document.createElement('canvas');
                            scaled.width = outWidth;
                            scaled.height = outHeight;
                            scaled.getContext('2d').drawImage(canvas, 0, 0, outWidth, outHeight);
                            encodeCanvas = scaled;
                        }
                        const dataUrl = await canvasToJpegDataUrl(encodeCanvas, quality);
                        if (scaled) { scaled.width = 0; scaled.height = 0; }
                        out.push({
                            page: pageNum,
                            imageId: name,
                            dataUrl,
                            // The dimensions of what the dataUrl ACTUALLY is, so a
                            // consumer sizing a thumbnail or reasoning about cost
                            // is not reading the pre-downscale numbers.
                            width: outWidth,
                            height: outHeight,
                            sourceWidth: width,
                            sourceHeight: height,
                            mimeType: 'image/jpeg',
                        });
                    } catch (err) {
                        encodeFailed++;
                        incompletePages.add(pageNum);
                        console.warn(`[PDF embed] canvas encode failed for ${name}:`, err?.message || err);
                    } finally {
                        canvas.width = 0;
                        canvas.height = 0;
                        // The decoded bitmap is ours once drawn, and we never draw
                        // the same id twice, so release it rather than waiting for
                        // page.cleanup to drop the whole registry.
                        try {
                            if (imgObj.bitmap && typeof imgObj.bitmap.close === 'function') imgObj.bitmap.close();
                        } catch { /* closing is best-effort memory hygiene */ }
                    }
                }
                await yieldToMain();
            } finally {
                // Drop this page's decoded XObjects before walking the next one.
                try {
                    if (typeof page.cleanup === 'function') page.cleanup();
                } catch (err) {
                    console.warn(`[PDF embed] page ${pageNum} cleanup failed:`, err?.message || err);
                }
            }
        }
    } finally {
        await releaseDocument();
    }

    const label = file.name || 'pdf';
    if (out.length > 0) {
        console.log(`[PDF embed] Extracted ${out.length} embedded image(s) from ${label} (walked ${pageCount} pages)`);
    } else if (imageOpsSeen > 0) {
        // Loud on purpose: the document DID paint images and we recovered none.
        console.warn(`[PDF embed] ${label}: ${imageOpsSeen} image draw(s) found but 0 recovered (unresolved ${unresolved}, page-render recovery required ${abovePixelCap}, encode failed ${encodeFailed})`);
    }
    const failedPageList = [...failedPages].sort((a, b) => a - b);
    const incompletePageList = [...incompletePages].sort((a, b) => a - b);
    const traversedEveryPage = visitedPages.length + failedPageList.length === pageCount;
    const complete = pageCount > 0
        && traversedEveryPage
        && failedPageList.length === 0
        && incompletePageList.length === 0;
    return finish(out, {
        schemaVersion: 1,
        status: complete ? 'complete' : (visitedPages.length > 0 ? 'partial' : 'failed'),
        totalPages: pageCount,
        visitedPages,
        failedPages: failedPageList,
        incompletePages: incompletePageList,
        imageDrawsSeen: imageOpsSeen,
        uniqueImagesAttempted: attempted.size,
        imagesExtracted: out.length,
        unresolvedImages: unresolved,
        belowMinDimension,
        abovePixelCap,
        pageRenderFallbackRequiredPages: [...pageRenderFallbackRequiredPages].sort((a, b) => a - b),
        encodeFailed,
    });
}

/**
 * Convert pdfjs raw image data to an RGBA Uint8ClampedArray for ImageData.
 * Returns null if the kind isn't supported.
 *
 * pdfjs image kinds:
 *   1 = GRAYSCALE_1BPP    (1 byte per pixel, 0=black, 255=white)
 *   2 = RGB_24BPP         (3 bytes per pixel)
 *   3 = RGBA_32BPP        (4 bytes per pixel - already RGBA)
 */
function _imgKindToRgba(img) {
    const { width, height, kind, data } = img;
    if (!data || !width || !height) return null;
    const total = width * height;
    if (kind === 3) {
        // Already RGBA - just wrap in Uint8ClampedArray if it isn't
        return data instanceof Uint8ClampedArray ? data : new Uint8ClampedArray(data);
    }
    if (kind === 2) {
        // RGB - expand to RGBA with alpha=255
        const out = new Uint8ClampedArray(total * 4);
        for (let i = 0, j = 0; i < total; i++, j += 4) {
            const k = i * 3;
            out[j] = data[k];
            out[j + 1] = data[k + 1];
            out[j + 2] = data[k + 2];
            out[j + 3] = 255;
        }
        return out;
    }
    if (kind === 1) {
        // Grayscale - replicate to R/G/B with alpha=255
        const out = new Uint8ClampedArray(total * 4);
        for (let i = 0, j = 0; i < total; i++, j += 4) {
            const v = data[i];
            out[j] = v; out[j + 1] = v; out[j + 2] = v; out[j + 3] = 255;
        }
        return out;
    }
    return null;
}

// ────────────────────────────────────────────────────────────────────────────
// Plan detection
// ────────────────────────────────────────────────────────────────────────────
// Used by the Stage 1 PDF import, the job's PDF evidence preparation and the
// QM Assistant attach handler to decide whether every page is read with the
// drawing prompt.
//
// CN3 (Peter 2026-10-13: "file names shouldnt dictate anything. only the
// contents."): decided by the document alone (detectPlanByContent): drawing
// marks in the text, how little text there is, and the page size. The file
// name is not an input. A page with no text layer is judged by its page size,
// so a scanned A4 sketch is read with the ordinary page reader.
// ────────────────────────────────────────────────────────────────────────────

/**
 * Multi-signal plan detection. Returns a likelihood object so callers can
 * make graduated decisions.
 *
 * @param {Object} doc
 * @param {string} [doc.textSample]  Extracted text (the first 8,000 characters are read)
 * @param {number} [doc.textLength]  Total extracted text length (drawings are sparse)
 * @param {Object} [doc.pageGeom]    Optional first-page { width, height } in PDF user units
 *                                   (typical A4 portrait ~595x842; A1 landscape
 *                                   ~2384x1684; drawings are usually >1500 wide)
 * @param {Array}  [doc.pages]       Optional page sizes, first page first (wins over pageGeom)
 * @returns {{ isPlan: boolean, confidence: number, signals: string[] }}
 */
export function detectPlanLikelihood({ textSample = '', textLength = null, pageGeom = null, pages = null } = {}) {
    const geometry = Array.isArray(pages) && pages.length > 0 ? pages : (pageGeom ? [pageGeom] : null);
    const result = detectPlanByContent({ text: textSample, textLength, pages: geometry });
    return { isPlan: result.isPlan, confidence: result.confidence, signals: result.signals };
}
